import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ICompany } from '../../../core/models/ICompany';
import { CompanyService } from '../../../core/services/company.service';
import { Router } from '@angular/router';
import { LogService } from '../../../core/services/log.service';

@Component({
  selector: 'app-admin-companies',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './admin-companies.html',
  styleUrl: './admin-companies.scss'
})
export class AdminCompanies implements OnInit {
  companies: ICompany[] = [];
  filteredCompanies: ICompany[] = [];
  paginatedCompanies: ICompany[] = [];
  selectedCompanies: Set<string> = new Set();
  
  searchTerm = '';
  filterCategory = '';
  filterStatus = '';
  
  currentPage = 1;
  pageSize = 10;
  totalPages = 1;
  Math = Math;

  stats = {
    total: 0,
    visible: 0,
    pending: 0,
    averageRating: 0
  };

  constructor(
    private companyService: CompanyService, 
    private router: Router,
    private logger: LogService
  ) {}

  ngOnInit() {
    this.loadCompanies();
  }

  loadCompanies() {
    this.companyService.getAllCompaniesForAdmin().subscribe(companies => {
      this.companies = companies;
      this.applyFilters();
      this.calculateStats();
    });
  }

  applyFilters() {
    let filtered = [...this.companies];

    if (this.searchTerm) {
      filtered = filtered.filter(c => 
        c.companyName.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        c.companyEmail.toLowerCase().includes(this.searchTerm.toLowerCase())
      );
    }

    if (this.filterCategory) {
      filtered = filtered.filter(c => c.category === this.filterCategory);
    }

    if (this.filterStatus === 'visible') {
      filtered = filtered.filter(c => c.isVisible);
    } else if (this.filterStatus === 'hidden') {
      filtered = filtered.filter(c => !c.isVisible);
    }

    this.filteredCompanies = filtered;
    this.totalPages = Math.ceil(filtered.length / this.pageSize);
    this.currentPage = 1; // Reset à la page 1 lors d'un filtre
    this.updatePaginatedCompanies();
  }

  updatePaginatedCompanies() {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedCompanies = this.filteredCompanies.slice(startIndex, endIndex);
  }

  onSearch() {
    this.applyFilters();
  }

  resetFilters() {
    this.searchTerm = '';
    this.filterCategory = '';
    this.filterStatus = '';
    this.applyFilters();
  }

  calculateStats() {
    this.stats.total = this.companies.length;
    this.stats.visible = this.companies.filter(c => c.isVisible).length;
    this.stats.pending = this.companies.filter(c => !c.isVisible).length;
    
    const sum = this.companies.reduce((acc, c) => acc + (c.averageRating || 0), 0);
    this.stats.averageRating = this.companies.length > 0 
      ? parseFloat((sum / this.companies.length).toFixed(1)) 
      : 0;
  }

  toggleSelectAll(event: any) {
    if (event.target.checked) {
      this.filteredCompanies.forEach(c => this.selectedCompanies.add(c._id));
    } else {
      this.selectedCompanies.clear();
    }
  }

  toggleSelect(id: string) {
    if (this.selectedCompanies.has(id)) {
      this.selectedCompanies.delete(id);
    } else {
      this.selectedCompanies.add(id);
    }
  }

  isSelected(id: string): boolean {
    return this.selectedCompanies.has(id);
  }

  viewCompany(company: ICompany) {
    this.router.navigate(['/company', company._id]);
  }

  editCompany(company: ICompany) {
    this.router.navigate(['/company/edit', company._id]);
  }

  toggleVisibility(company: ICompany) {
    company.isVisible = !company.isVisible;
    this.companyService.editCompany(company._id, { isVisible: company.isVisible })
      .subscribe(() => this.calculateStats());
  }

  deleteCompany(company: ICompany) {
    if (confirm(`Êtes-vous sûr de vouloir supprimer "${company.companyName}" ?`)) {
      this.companyService.deleteCompany(company._id).subscribe(() => {
        this.loadCompanies();
      });
    }
  }

  exportData() {
    this.logger.log('Export data');
  }

  getPages(): number[] {
    const maxPages = 5;
    const pages: number[] = [];
    
    let startPage = Math.max(1, this.currentPage - Math.floor(maxPages / 2));
    let endPage = Math.min(this.totalPages, startPage + maxPages - 1);
    
    if (endPage - startPage < maxPages - 1) {
      startPage = Math.max(1, endPage - maxPages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  }

  goToPage(page: number) {
    this.currentPage = page;
    this.updatePaginatedCompanies();
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.updatePaginatedCompanies();
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.updatePaginatedCompanies();
    }
  }
}